import React from "react";
import { Box, Typography } from "@mui/material";
import { C } from "../common/theme";
import { EmptyState } from "./States";

export interface BarItem {
  key: string;
  label: React.ReactNode;
  value: number;
  /** Shown on the right instead of the raw value */
  display?: React.ReactNode;
  sub?: React.ReactNode;
  onClick?: () => void;
}

/** A ranked list with a bar behind each row (top sellers, top pages...). */
export default function BarList({ items, empty = "Nothing yet.", max }: { items: BarItem[]; empty?: string; max?: number }) {
  if (!items.length) return <EmptyState title={empty} />;
  const top = max ?? Math.max(...items.map((i) => i.value), 1);
  return (
    <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
      {items.map((item) => {
        const pct = Math.max(2, Math.round((item.value / top) * 100));
        const Row = item.onClick ? "button" : "div";
        return (
          <li key={item.key}>
            <Box
              component={Row}
              type={item.onClick ? "button" : undefined}
              onClick={item.onClick}
              sx={{
                all: item.onClick ? "unset" : undefined,
                boxSizing: "border-box",
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                width: "100%",
                minHeight: 40,
                px: 1.25,
                py: 0.75,
                borderRadius: "6px",
                overflow: "hidden",
                cursor: item.onClick ? "pointer" : "default",
                "&:hover .bar": item.onClick ? { bgcolor: C.bone } : undefined,
                "&:focus-visible": { outline: `2px solid ${C.coral}` },
              }}
            >
              <Box className="bar" sx={{ position: "absolute", inset: 0, width: `${pct}%`, bgcolor: C.bone1, borderRadius: "6px", transition: "background-color .15s" }} />
              <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {item.label}
                </Typography>
                {item.sub && (
                  <Typography variant="caption" sx={{ color: C.muted }}>
                    {item.sub}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" sx={{ position: "relative", fontWeight: 700, whiteSpace: "nowrap" }}>
                {item.display ?? item.value}
              </Typography>
            </Box>
          </li>
        );
      })}
    </Box>
  );
}
